/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const DEACTIVATE_DID_DOCUMENT_DISCRIMINATOR = new Uint8Array([
  77, 13, 56, 161, 67, 155, 206, 119,
]);

export function getDeactivateDidDocumentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DEACTIVATE_DID_DOCUMENT_DISCRIMINATOR,
  );
}

export type DeactivateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountDidDocument extends string | AccountMeta<string> = string,
  TAccountController extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountDidDocument extends string
        ? WritableAccount<TAccountDidDocument>
        : TAccountDidDocument,
      TAccountController extends string
        ? WritableSignerAccount<TAccountController> &
            AccountSignerMeta<TAccountController>
        : TAccountController,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type DeactivateDidDocumentInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type DeactivateDidDocumentInstructionDataArgs = {};

export function getDeactivateDidDocumentInstructionDataEncoder(): FixedSizeEncoder<DeactivateDidDocumentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: DEACTIVATE_DID_DOCUMENT_DISCRIMINATOR,
    }),
  );
}

export function getDeactivateDidDocumentInstructionDataDecoder(): FixedSizeDecoder<DeactivateDidDocumentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getDeactivateDidDocumentInstructionDataCodec(): FixedSizeCodec<
  DeactivateDidDocumentInstructionDataArgs,
  DeactivateDidDocumentInstructionData
> {
  return combineCodec(
    getDeactivateDidDocumentInstructionDataEncoder(),
    getDeactivateDidDocumentInstructionDataDecoder(),
  );
}

export type DeactivateDidDocumentAsyncInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA validation */
  didDocument?: Address<TAccountDidDocument>;
  /** Controller of the DID (must match document controller) */
  controller: TransactionSigner<TAccountController>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
};

export async function getDeactivateDidDocumentInstructionAsync<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateDidDocumentAsyncInput<
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  DeactivateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.didDocument.value) {
    accounts.didDocument.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            100, 105, 100, 95, 100, 111, 99, 117, 109, 101, 110, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.controller.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.clock),
    ],
    data: getDeactivateDidDocumentInstructionDataEncoder().encode({}),
    programAddress,
  } as DeactivateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >);
}

export type DeactivateDidDocumentInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA validation */
  didDocument: Address<TAccountDidDocument>;
  /** Controller of the DID (must match document controller) */
  controller: TransactionSigner<TAccountController>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
};

export function getDeactivateDidDocumentInstruction<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateDidDocumentInput<
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): DeactivateDidDocumentInstruction<
  TProgramAddress,
  TAccountDidDocument,
  TAccountController,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.clock),
    ],
    data: getDeactivateDidDocumentInstructionDataEncoder().encode({}),
    programAddress,
  } as DeactivateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >);
}

export type ParsedDeactivateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** DID document account with canonical PDA validation */
    didDocument: TAccountMetas[0];
    /** Controller of the DID (must match document controller) */
    controller: TAccountMetas[1];
    /** Clock sysvar for timestamp validation */
    clock: TAccountMetas[2];
  };
  data: DeactivateDidDocumentInstructionData;
};

export function parseDeactivateDidDocumentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedDeactivateDidDocumentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      didDocument: getNextAccount(),
      controller: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getDeactivateDidDocumentInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
